import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendUrl';
import { formatCurrency } from '../utils/currency';
import ModalCloseButton from './ui/ModalCloseButton';

// Simple Icons to avoid external deps
const Icons = {
    Search: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
    Back: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>,
    Activity: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    Basket: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>,
    Plus: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>,
    Filter: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>,
    Selected: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
};

const normalizeCatalogTab = (tab, hasSelected) => {
    if (tab === 'selected' && hasSelected) return 'selected';
    if (tab === 'activities') return 'activities';
    return 'items';
};

const getDisplayCatalogItemName = (item) => {
  const name = item?.name || '';

  if (item?.kind !== 'decor_item') return name;

  const genericDecorNames = [
    'Entry Sign Moment',
    'Backdrop Zone',
    'Centerpiece Cluster',
    'Ambient Lighting Layer',
    'Table Accent Kit',
    'Music Mood Cue',
    'Aroma And Comfort Cue',
    'Favor Display',
    'Balloon Or Paper Accent',
    'Host Station Tray'
  ];

  const match = genericDecorNames.find(baseName => name.endsWith(baseName));
  return match || name;
};

const dedupeCatalogItemsForDisplay = (items = []) => {
  const seen = new Map();

  items.forEach(item => {
    if (item?.kind !== 'decor_item') {
      const key = item?._id || item?.id || item?.slug || item?.name;
      seen.set(key, item);
      return;
    }

    const displayName = getDisplayCatalogItemName(item);
    const key = `decor:${displayName}`;

    if (!seen.has(key)) {
      seen.set(key, item);
    }
  });

  return Array.from(seen.values());
};

const getCatalogKey = (item, type) => {
    const rawId = item?.catalog_id || item?._id || item?.id || item?.name || '';
    return `${type}:${String(rawId).toLowerCase()}`;
};

const getExistingCatalogKey = (item) => {
    if (item?.catalog_key) return item.catalog_key;
    if (item?.catalog_type && item?.catalog_id) return getCatalogKey(item, item.catalog_type);
    const type = item?.category === 'Activities' ? 'activity' : 'item';
    return `${type}:${String(item?.name || '').toLowerCase()}`;
};

const normalizeSupplyName = (name) => String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const toTitleCaseLabel = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const getSupplyUsageType = (supplyName) => {
    const name = String(supplyName || '').toLowerCase();

    if (/(marker|pen|pencil|scissor|tape|speaker|clipboard|timer|bin|basket|tray|board|dice|stamp|ruler)/.test(name)) {
        return 'reusable';
    }

    if (/(paper|sticker|label|tag|sheet|card|printable|cup|plate|napkin|favor|token|ticket|balloon)/.test(name)) {
        return 'consumable';
    }

    if (/(sign|prop|stand|box|jar|bucket|prize)/.test(name)) {
        return 'fixed';
    }

    return 'reusable';
};

const buildActivitySupplyItems = (activityItem, parentShoppingItem, catalogKey) => {
    const activityId = activityItem?._id || activityItem?.id || activityItem?.slug || activityItem?.name || 'activity';
    const rawSupplies = Array.isArray(activityItem?.requiredSupplies) && activityItem.requiredSupplies.length
        ? activityItem.requiredSupplies
        : (Array.isArray(activityItem?.supply_queries) ? activityItem.supply_queries.map(name => ({ name })) : []);

    return rawSupplies
        .map((supply) => {
            const supplyName = typeof supply === 'string' ? supply : supply?.name;
            const normalizedName = normalizeSupplyName(supplyName);
            if (!supplyName || !normalizedName) return null;
            const quantityPolicy = typeof supply === 'string' ? null : supply?.quantityPolicy;
            const quantity = String(quantityPolicy?.unit || '').toLowerCase() === 'per_activity_group'
                ? (activityItem?.planningEstimate?.quantity || parentShoppingItem?.quantity || 1)
                : (quantityPolicy?.base || 1);
            const supplyKey = `activity_supply:${normalizedName}`;
            return {
                id: `cat_${supplyKey}`,
                name: toTitleCaseLabel(supplyName),
                category: 'Activity Supplies',
                description: `Needed for ${activityItem.name}.`,
                catalog_type: 'activity_supply',
                supply_key: supplyKey,
                catalog_key: supplyKey,
                catalog_ref: supplyKey,
                catalog_id: supplyKey,
                supply_usage_type: getSupplyUsageType(supplyName),
                linked_activities: [
                    {
                        key: catalogKey,
                        name: activityItem.name,
                        quantity
                    }
                ],
                parent_activity_key: catalogKey,
                parent_activity_name: activityItem.name,
                parent_activity_ref: parentShoppingItem?.catalog_ref || parentShoppingItem?.catalog_key || catalogKey,
                parent_activity_id: parentShoppingItem?.catalog_id || activityItem?._id || activityItem?.id || activityItem?.slug || activityItem?.name,
                workflow_origin: 'activity',
                origin: 'activity',
                source: 'activity_required_supply',
                purchaseType: 'buy_or_make',
                quantity,
                quantity_reason: `Needed for ${activityItem.name}`,
                // Display-only hint: cost is rolled into the parent activity estimate.
                // No buy_price is set here intentionally — do not add one without real per-supply unit pricing.
                estimate_label: 'Included in activity estimate',
                estimate_source: 'CraftMyParty activity supply estimate',
                sourcing_channels: parentShoppingItem.sourcing_channels || ['retail_online', 'local_retail']
            };
        })
        .filter(Boolean);
};

const ITEM_SUPPLY_CATEGORIES = [
    'Decor',
    'Food & Cake',
    'Tableware',
    'Rentals',
    'Logistics',
    'Printables'
];

const CATALOG_DEPARTMENTS = [
    { label: 'Activities', value: 'Activities', tab: 'activities', category: 'Activities' },
    { label: 'Decor', value: 'Decor', tab: 'items', category: 'Decor' },
    { label: 'Food', value: 'Food', tab: 'items', category: 'Food & Cake' },
    { label: 'Tableware', value: 'Tableware', tab: 'items', category: 'Tableware' },
    { label: 'Rentals', value: 'Rentals', tab: 'items', category: 'Rentals' },
    { label: 'Setup', value: 'Setup', tab: 'items', category: 'Logistics' },
    { label: 'Printables', value: 'Printables', tab: 'items', category: 'Printables' }
];

const getCatalogDepartmentLabel = (activeTab, itemCategory) => {
    if (activeTab === 'selected') return 'Selected from catalog';
    if (activeTab === 'activities') return 'Activities';
    if (itemCategory === 'Food & Cake') return 'Food';
    if (itemCategory === 'Logistics') return 'Setup';
    return itemCategory;
};

const getCatalogItemSourceLabel = (item) => {
    if (item.workflow_origin === 'catalog') {
        return 'Added by you';
    }
    if (
        item.workflow_origin === 'decor' ||
        item.workflow_origin === 'food' ||
        item.workflow_origin === 'activities' ||
        item.source === 'ai' ||
        item.generated_by === 'ai' ||
        item.is_ai_generated ||
        item.origin === 'generated' ||
        item.workflow_origin === 'generated'
    ) {
        return 'Suggested by plan';
    }
    return 'Catalog item';
};

const getCatalogItemTypeLabel = (item, activeTab, itemCategory) => {
    if (
        activeTab !== 'activities'
        && itemCategory === 'Food & Cake'
        && item?.kind === 'service'
    ) {
        if (item?.subCategory === 'catering') return 'Catering Service';
        if (item?.subCategory === 'bakery') return 'Bakery Service';
        return 'Food Service';
    }

    return item?.kind ? item.kind.replace(/_/g, ' ') : item?.category;
};

const normalizeItemCategory = (category) => {
    if (category === 'Food') return 'Food & Cake';
    return category;
};

const getCatalogVisualMeta = (item) => {
    const category = item?.category || item?.kind || 'Catalog';
    if (category === 'Food & Cake' || item?.kind === 'food_plan') return { icon: '🍽️', label: 'Food idea' };
    if (category === 'Decor' || item?.kind === 'decor_item') return { icon: '✨', label: 'Decor' };
    if (category === 'Tableware' || item?.kind === 'tableware') return { icon: '🍽', label: 'Tableware' };
    if (category === 'Logistics' || item?.kind === 'logistics') return { icon: '🧰', label: 'Setup' };
    if (category === 'Rentals' || item?.kind === 'rental') return { icon: '🪑', label: 'Rental' };
    return { icon: '🛍️', label: String(category).replace(/_/g, ' ') };
};

const mapEventTypeForCatalog = (eventType) => {
    if (!eventType) return '';
    const lower = String(eventType).toLowerCase();
    if (lower.includes('birthday')) return 'birthday';
    if (lower.includes('wedding')) return 'wedding';
    if (lower.includes('baby')) return 'baby';
    if (lower.includes('bridal')) return 'bridal';
    if (lower.includes('corporate')) return 'corporate';
    if (lower.includes('anniversary')) return 'anniversary';
    if (lower.includes('holiday')) return 'holiday';
    if (lower.includes('dinner')) return 'dinner';
    if (lower.includes('graduation')) return 'graduation';
    return lower.replace(/\s+party$/i, '').trim() || lower;
};

const ACTIVITY_CATEGORY_ORDER = [
    'All',
    'Popular',
    'Dinner Party',
    'Games',
    'Creative & DIY',
    'Seasonal',
    'Icebreakers',
    'Kids & Family',
    'Corporate',
    'Outdoor',
    'Music & Atmosphere',
    'Photo & Memories'
];

const getActivityCategory = (activity) => {
    const explicit = activity?.subCategory || activity?.category;
    if (explicit) return explicit;
    const tags = (activity?.tags || []).map(tag => String(tag).toLowerCase());
    if (tags.includes('popular')) return 'Popular';
    if (tags.includes('dinner-party') || tags.includes('food')) return 'Dinner Party';
    if (tags.includes('games') || tags.includes('game') || tags.includes('teams')) return 'Games';
    if (tags.includes('creative') || tags.includes('craft') || tags.includes('diy')) return 'Creative & DIY';
    if (tags.includes('seasonal') || tags.includes('outdoor')) return 'Seasonal';
    if (tags.includes('icebreaker') || tags.includes('conversation')) return 'Icebreakers';
    if (tags.includes('kids') || tags.includes('family')) return 'Kids & Family';
    if (tags.includes('corporate') || tags.includes('professional')) return 'Corporate';
    if (String(activity?.energy_level || '').toLowerCase() === 'high') return 'Games';
    return 'Popular';
};

const estimateActivitySupplyCost = (activity, guestCount = 10) => {
    if (activity?.planningEstimate?.total_estimate !== undefined) {
        return Number(activity.planningEstimate.total_estimate || 0);
    }
    const supplies = Array.isArray(activity?.supply_queries) ? activity.supply_queries : [];
    const structuredSupplies = Array.isArray(activity?.requiredSupplies) ? activity.requiredSupplies : [];
    if (structuredSupplies.length > 0 && supplies.length === 0) {
        const base = Number(activity?.minPrice || 10);
        const guestFactor = Math.max(0, Math.ceil((Number(guestCount) || 10) / 10) - 1) * 4;
        return Math.min(90, base + guestFactor + structuredSupplies.length * 3);
    }
    if (supplies.length === 0) return 0;

    const baseByCategory = {
        'Creative & DIY': 22,
        'Kids & Family': 18,
        'Games': 14,
        'Photo & Memories': 18,
        'Outdoor': 28,
        'Music & Atmosphere': 12,
        'Dinner Party': 10,
        'Icebreakers': 8,
        'Corporate': 12,
        'Seasonal': 14
    };
    const category = getActivityCategory(activity);
    const base = baseByCategory[category] || 12;
    const guestFactor = Math.max(0, Math.ceil((Number(guestCount) || 10) / 10) - 1) * 4;
    const supplyFactor = Math.max(0, supplies.length - 2) * 3;
    return Math.min(90, base + guestFactor + supplyFactor);
};

const parseInstructionSteps = (instructions) => {
    if (Array.isArray(instructions)) return instructions.filter(Boolean);
    if (!instructions) return [];
    const text = String(instructions).trim();
    if (!text) return [];

    const numberedSteps = text
        .split(/\s*(?:^|\s)\d+[.)]\s+/)
        .map(step => step.trim())
        .filter(Boolean);

    if (numberedSteps.length > 1) return numberedSteps;

    return text
        .split(/\s*[;•]\s+/)
        .map(step => step.trim())
        .filter(Boolean);
};

const CatalogExplorer = ({ 
    isOpen, 
    onClose, 
    onSelectItem, 
    eventData, 
    initialTab = 'items', 
    initialCategory = '', 
    initialSearch = '', 
    existingItems = [],
    onTabChange,
    onCategoryChange
}) => {
    const [activeTab, setActiveTab] = useState(normalizeCatalogTab(initialTab)); // activities, items
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const country = eventData?.country || 'US';
    const formatCatalogPrice = (value) => {
        const amount = Number(value || 0);
        const decimals = Math.abs(amount % 1) > 0.000001 ? 2 : 0;
        return formatCurrency(amount, country, { decimals });
    };
    // Reconstruct price display from shopping-list item fields (which store pre-formatted strings).
    // Mirrors the { primary, secondary } shape of getCatalogPriceDisplay so Selected cards
    // show the same estimate text as normal category cards.
    const getSelectedItemPriceDisplay = (item) => {
        // buy_price is stored as a formatted string like "$8" or "$32".
        // price_per_unit is also a formatted string.
        // quantity is a number (stored at add time from planningEstimate.quantity).
        const rawTotal = item?.buy_price;
        const rawUnit = item?.price_per_unit;
        const qty = Number(item?.quantity || 1);

        // Guard: reject strings that are empty, null, or unparseable numbers.
        const isSafePrice = (val) => {
            if (!val || typeof val !== 'string') return false;
            // Strip currency symbol/whitespace and try to parse.
            const numeric = parseFloat(String(val).replace(/[^0-9.,-]/g, '').replace(',', '.'));
            return !isNaN(numeric) && numeric >= 0;
        };

        if (!isSafePrice(rawTotal) && !isSafePrice(rawUnit)) {
            return { primary: '', secondary: '' };
        }

        // Primary: "Est. $X total" using buy_price (which == total at add time).
        // If it already starts with "Est." leave it as-is.
        const priceStr = rawTotal || rawUnit;
        const primary = String(priceStr).startsWith('Est.')
            ? priceStr
            : `Est. ${priceStr} total`;

        // Secondary: "$Y/item · N items" when qty > 1, else "$Y/item".
        let secondary = '';
        if (isSafePrice(rawUnit)) {
            secondary = qty > 1
                ? `${rawUnit}/item \u00b7 ${qty} items`
                : `${rawUnit}/item`;
        }

        return { primary, secondary };
    };
    const formatCatalogAmount = (value, currencyCode) => {
        const amount = Number(value || 0);
        const decimals = Math.abs(amount % 1) > 0.000001 ? 2 : 0;
        if (!currencyCode) return formatCatalogPrice(amount);
        try {
            return new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: String(currencyCode).toUpperCase(),
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            }).format(amount);
        } catch (error) {
            return formatCatalogPrice(amount);
        }
    };
    const formatEstimatedCatalogPrice = (value) => `Est. ${formatCatalogPrice(value)}`;
    const getEstimateUnitLabel = (item, planningEstimate) => {
        if (item?.kind === 'food_plan') {
            return { singular: 'serving', plural: 'servings' };
        }

        const model = String(
            planningEstimate?.pricing_model
            || item?.pricing?.model
            || item?.quantityPolicy?.unit
            || ''
        ).toLowerCase();

        if (model === 'per_guest') return { singular: 'guest', plural: 'guests' };
        if (model === 'per_table') return { singular: 'table', plural: 'tables' };
        if (model === 'per_activity_group') return { singular: 'group', plural: 'groups' };
        if (model === 'per_event') return { singular: 'event', plural: 'event' };
        if (model === 'digital_or_local_print') return { singular: 'printable', plural: 'printables' };

        return { singular: 'item', plural: 'items' };
    };
    const getCatalogPriceDisplay = (item) => {
        const estimate = item?.planningEstimate;
        const currencyCode = estimate?.currency;
        if (estimate?.total_estimate !== undefined && estimate?.total_estimate !== null) {
            const primary = `Est. ${formatCatalogAmount(estimate.total_estimate, currencyCode)} total`;
            const unitLabel = getEstimateUnitLabel(item, estimate);
            const unitPrice = formatCatalogAmount(
                estimate.unit_price_estimate ?? item?.minPrice ?? item?.maxPrice ?? 0,
                currencyCode
            );
            let secondary = `${unitPrice}/${unitLabel.singular}`;
            const quantity = Number(estimate?.quantity || 0);
            if (quantity > 1) {
                secondary += ` · ${quantity} ${unitLabel.plural}`;
            }
            return { primary, secondary };
        }
        if (item?.minPrice || item?.maxPrice) {
            return {
                primary: formatEstimatedCatalogPrice(item.minPrice ?? item.maxPrice),
                secondary: ''
            };
        }
        return { primary: '', secondary: '' };
    };

    // Data states
    const [activities, setActivities] = useState([]);
    const [items, setItems] = useState([]);
    const [itemsContextKey, setItemsContextKey] = useState('');
    const [itemsRequestState, setItemsRequestState] = useState({
        key: '',
        status: 'idle'
    });
    const [activitiesRequestState, setActivitiesRequestState] = useState({
        status: 'idle'
    });
    const [addedKeys, setAddedKeys] = useState(new Set());
    const sessionCacheRef = React.useRef({ items: {}, activities: null });

    const selectedCatalogItems = (existingItems || []).filter(item => 
        (item.workflow_origin === 'catalog' || item.origin === 'catalog' || item.catalog_key || item.catalog_ref) 
        && item.catalog_type !== 'activity_supply'
    );

    // Filters
    const [ageFilter, setAgeFilter] = useState('');
    const [eventFilter, setEventFilter] = useState('');
    const [selectedActivityCategories, setSelectedActivityCategories] = useState(['All']);
    const [selectedItemCategory, setSelectedItemCategory] = useState('All');
    const [selectedFoodSubCategory, setSelectedFoodSubCategory] = useState('All');
    const [selectedSourceFilter, setSelectedSourceFilter] = useState('All');
    const [itemsPage, setItemsPage] = useState(1);
    const [itemsHasMore, setItemsHasMore] = useState(false);
    const ITEMS_PAGE_SIZE = 40;
    const [showActivityFilterMenu, setShowActivityFilterMenu] = useState(false);
    const latestFetchIdRef = React.useRef(0);
    const lastStartedFetchKeyRef = React.useRef('');
    const filterMenuRef = React.useRef(null);
    const buildItemsContextKey = ({ itemCategory, subCategory, search, eventType }) =>
        [
            'items',
            itemCategory || '',
            subCategory || '',
            search || '',
            eventType || ''
        ].join('|');

    useEffect(() => {
        if (isOpen) {
            sessionCacheRef.current = { items: {}, activities: null };
            // Initialize filters from eventData
            if (eventData?.age_range) setAgeFilter(eventData.age_range);
            if (eventData?.event_type) setEventFilter(eventData.event_type);
            const hasSelected = (existingItems || []).some(item => 
                (item.workflow_origin === 'catalog' || item.origin === 'catalog' || item.catalog_key || item.catalog_ref) 
                && item.catalog_type !== 'activity_supply'
            );
            const safeInitialTab = normalizeCatalogTab(initialTab, hasSelected);
            setActiveTab(safeInitialTab);
            setSelectedActivityCategories(safeInitialTab === 'activities' && initialCategory ? [initialCategory] : ['All']);

            setSearchQuery(initialSearch || '');
            const normalizedInitialCategory = normalizeItemCategory(initialCategory);
            const initialItemCategory = normalizedInitialCategory && ITEM_SUPPLY_CATEGORIES.includes(normalizedInitialCategory)
                ? normalizedInitialCategory
                : 'Decor';
            setSelectedItemCategory(initialItemCategory);
            setSelectedFoodSubCategory('All');
            setSelectedSourceFilter('All');
            setItemsPage(1);
            setItems([]);
            setActivities([]);
            setItemsContextKey('');
            setItemsRequestState({ key: '', status: 'idle' });
            setActivitiesRequestState({ status: 'idle' });
            lastStartedFetchKeyRef.current = '';
        }
    }, [isOpen, eventData, initialTab, initialCategory, initialSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setAddedKeys(new Set((existingItems || []).map(getExistingCatalogKey).filter(Boolean)));
    }, [existingItems]);

    useEffect(() => {
        if (onTabChange) onTabChange(activeTab);
    }, [activeTab, onTabChange]);

    useEffect(() => {
        if (onCategoryChange) onCategoryChange(selectedItemCategory);
    }, [selectedItemCategory, onCategoryChange]);

    useEffect(() => {
        if (activeTab === 'selected' && selectedCatalogItems.length === 0) {
            setActiveTab('items');
            setSelectedItemCategory('Decor');
        }
    }, [activeTab, selectedCatalogItems.length]);

    useEffect(() => {
        if (activeTab === 'selected') {
            const addedByYouCount = selectedCatalogItems.filter(item => getCatalogItemSourceLabel(item) === 'Added by you').length;
            const suggestedByPlanCount = selectedCatalogItems.filter(item => getCatalogItemSourceLabel(item) === 'Suggested by plan').length;
            const fallbackCount = selectedCatalogItems.filter(item => getCatalogItemSourceLabel(item) === 'Catalog item').length;

            const activeFilterCount = 
                selectedSourceFilter === 'All' ? selectedCatalogItems.length :
                selectedSourceFilter === 'Added by you' ? addedByYouCount :
                selectedSourceFilter === 'Suggested by plan' ? suggestedByPlanCount :
                fallbackCount;

            if (activeFilterCount === 0) {
                setSelectedSourceFilter('All');
            }
        }
    }, [activeTab, selectedCatalogItems, selectedSourceFilter]);

    const fetchData = useCallback(async (type, overrides = {}) => {
        const requestId = latestFetchIdRef.current + 1;
        latestFetchIdRef.current = requestId;
        let responseContextKey = '';
        setLoading(true);
        try {
            let endpoint = '';
            let params = {};
            const effectiveSearch = overrides.search ?? searchQuery;
            const effectiveEventFilter = overrides.eventType ?? eventFilter;
            const itemCategory = overrides.itemCategory ?? selectedItemCategory;
            const subCategory = overrides.subCategory ?? selectedFoodSubCategory;
            const page = overrides.page ?? 1;
            const appendItems = Boolean(overrides.appendItems);
            responseContextKey = type === 'items'
                ? buildItemsContextKey({
                    itemCategory,
                    subCategory,
                    search: effectiveSearch,
                    eventType: effectiveEventFilter
                })
                : '';

            if (type === 'items') {
                setItemsRequestState({
                    key: responseContextKey,
                    status: 'loading'
                });
                if (!appendItems) {
                    setItemsContextKey('');
                }
            }

            if (type === 'activities') {
                setActivitiesRequestState({ status: 'loading' });
            }

            if (type === 'activities') {
                endpoint = '/api/catalog/search';
                params.kind = 'activity';
                params.workflow = 'activities';
                params.limit = 80;
                const mappedEvent = mapEventTypeForCatalog(effectiveEventFilter);
                if (mappedEvent) params.eventType = mappedEvent;
                if (eventData?.budget) params.budget = eventData.budget;
                if (eventData?.guest_count) params.guestCount = eventData.guest_count;
                params.country = eventData?.country || 'US';
                if (eventData?.age_range) params.ageRange = eventData.age_range;
                if (eventData?.dietary_restrictions) {
                    params.dietaryRestrictions = Array.isArray(eventData.dietary_restrictions)
                        ? eventData.dietary_restrictions.join(',')
                        : String(eventData.dietary_restrictions);
                }
                if (eventData?.event_specifics) params.eventSpecifics = JSON.stringify(eventData.event_specifics);
            } else {
                endpoint = '/api/catalog/search';
                params.limit = ITEMS_PAGE_SIZE;
                params.page = page;
                params.workflow = 'shopping';
                if (itemCategory && itemCategory !== 'All') params.category = itemCategory;
                if (itemCategory === 'Food & Cake' && subCategory && subCategory !== 'All') params.subCategory = subCategory;
                const mappedEvent = mapEventTypeForCatalog(effectiveEventFilter);
                if (mappedEvent) params.eventType = mappedEvent;
                if (eventData?.budget) params.budget = eventData.budget;
                if (eventData?.guest_count) params.guestCount = eventData.guest_count;
                params.country = eventData?.country || 'US';
                if (Array.isArray(eventData?.food_source)) params.foodSource = eventData.food_source.join(',');
                if (Array.isArray(eventData?.cuisine_types)) params.cuisineTypes = eventData.cuisine_types.join(',');
                if (Array.isArray(eventData?.meals)) params.meals = eventData.meals.join(',');
                if (eventData?.age_range) params.ageRange = eventData.age_range;
                if (eventData?.dietary_restrictions) {
                    params.dietaryRestrictions = Array.isArray(eventData.dietary_restrictions)
                        ? eventData.dietary_restrictions.join(',')
                        : String(eventData.dietary_restrictions);
                }
                if (eventData?.event_specifics) params.eventSpecifics = JSON.stringify(eventData.event_specifics);
            }

            if (effectiveSearch) params.q = effectiveSearch;

            console.log('[Catalog] fetch start', {
                requestId,
                type,
                endpoint,
                params,
                responseContextKey,
                activeTab,
                selectedItemCategory,
                selectedFoodSubCategory
            });

            const response = await axios.get(`${BACKEND_URL}${endpoint}`, { params });

            if (requestId !== latestFetchIdRef.current) {
                console.log('[Catalog] stale response ignored', {
                    requestId,
                    latestRequestId: latestFetchIdRef.current,
                    type,
                    responseContextKey
                });
                return;
            }

            console.log('[Catalog] fetch success latest', {
                requestId,
                type,
                params,
                responseContextKey,
                count: Array.isArray(response.data) ? response.data.length : null,
                firstItems: Array.isArray(response.data)
                    ? response.data.slice(0, 3).map(item => ({
                        name: item.name,
                        category: item.category,
                        kind: item.kind
                    }))
                    : null
            });

            if (type === 'activities') {
                sessionCacheRef.current.activities = response.data;
                setActivities(response.data);
                setActivitiesRequestState({ status: 'success' });
            }
            if (type === 'items') {
                const batch = response.data || [];
                const prevCached = sessionCacheRef.current.items[responseContextKey]?.items || [];
                const nextItems = appendItems ? [...prevCached, ...batch] : batch;
                sessionCacheRef.current.items[responseContextKey] = {
                    items: nextItems,
                    page,
                    hasMore: batch.length === ITEMS_PAGE_SIZE
                };
                setItems(nextItems);
                setItemsContextKey(responseContextKey);
                setItemsPage(page);
                setItemsHasMore(batch.length === ITEMS_PAGE_SIZE);
                setItemsRequestState({
                    key: responseContextKey,
                    status: 'success'
                });
            }

        } catch (error) {
            if (requestId === latestFetchIdRef.current) {
                if (type === 'items') {
                    setItemsRequestState({
                        key: responseContextKey,
                        status: 'error'
                    });
                }
                if (type === 'activities') {
                    setActivitiesRequestState({ status: 'error' });
                }
                console.error(`Error fetching ${type}:`, error);
            }
        } finally {
            if (requestId === latestFetchIdRef.current) {
                setLoading(false);
            }
        }
    }, [
        searchQuery,
        eventFilter,
        selectedItemCategory,
        eventData?.budget,
        eventData?.guest_count,
        eventData?.food_source,
        eventData?.cuisine_types,
        eventData?.meals,
        selectedFoodSubCategory
    ]);

    useEffect(() => {
        if (!isOpen) return;
        if (activeTab === 'selected') return;

        const fetchKey = [
            activeTab,
            selectedItemCategory || '',
            selectedFoodSubCategory || '',
            searchQuery || '',
            eventFilter || '',
            eventData?.budget || '',
            eventData?.guest_count || '',
            eventData?.food_source || '',
            Array.isArray(eventData?.cuisine_types) ? eventData.cuisine_types.join(',') : eventData?.cuisine_types || '',
            Array.isArray(eventData?.meals) ? eventData.meals.join(',') : eventData?.meals || ''
        ].join('|');

        const timeoutId = setTimeout(() => {
            if (lastStartedFetchKeyRef.current === fetchKey) {
                return;
            }

            // Check cache
            if (activeTab === 'activities') {
                if (sessionCacheRef.current.activities) {
                    setActivities(sessionCacheRef.current.activities);
                    setActivitiesRequestState({ status: 'success' });
                    lastStartedFetchKeyRef.current = fetchKey;
                    return;
                }
            } else {
                const responseContextKey = buildItemsContextKey({
                    itemCategory: selectedItemCategory,
                    subCategory: selectedFoodSubCategory,
                    search: searchQuery,
                    eventType: eventFilter
                });
                const cached = sessionCacheRef.current.items[responseContextKey];
                if (cached) {
                    setItems(cached.items);
                    setItemsPage(cached.page);
                    setItemsHasMore(cached.hasMore);
                    setItemsContextKey(responseContextKey);
                    setItemsRequestState({
                        key: responseContextKey,
                        status: 'success'
                    });
                    lastStartedFetchKeyRef.current = fetchKey;
                    return;
                }
            }

            lastStartedFetchKeyRef.current = fetchKey;

            console.log('[Catalog] effect fetch decision', {
                isOpen,
                activeTab,
                selectedItemCategory,
                selectedFoodSubCategory,
                searchQuery,
                eventFilter
            });

            if (activeTab === 'activities') {
                fetchData('activities');
                return;
            }

            fetchData('items', {
                itemCategory: selectedItemCategory,
                subCategory: selectedFoodSubCategory,
                search: searchQuery,
                eventType: eventFilter,
                page: 1
            });
        }, 400);

        return () => clearTimeout(timeoutId);
    }, [
        isOpen,
        activeTab,
        selectedItemCategory,
        selectedFoodSubCategory,
        searchQuery,
        eventFilter,
        eventData?.budget,
        eventData?.guest_count,
        eventData?.food_source,
        eventData?.cuisine_types,
        eventData?.meals,
        fetchData
    ]);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchData(activeTab);
    };

    const handleDepartmentSelect = (department) => {
        console.log('[Catalog] department click', {
            label: department.label,
            tab: department.tab,
            category: department.category,
            value: department.value
        });

        if (department.tab === 'selected') {
            setActiveTab('selected');
            setSelectedItemCategory('Selected');
            return;
        }

        if (department.tab === 'activities') {
            if (sessionCacheRef.current.activities) {
                setActiveTab('activities');
                setActivities(sessionCacheRef.current.activities);
                setActivitiesRequestState({ status: 'success' });
                return;
            }
            setActiveTab('activities');
            setActivities([]);
            setItems([]);
            setItemsContextKey('');
            setItemsHasMore(false);
            setActivitiesRequestState({ status: 'loading' });
            return;
        }

        const targetContextKey = buildItemsContextKey({
            itemCategory: department.category,
            subCategory: 'All',
            search: searchQuery,
            eventType: eventFilter
        });
        const cached = sessionCacheRef.current.items[targetContextKey];
        if (cached) {
            setActiveTab('items');
            setSelectedItemCategory(department.category);
            setSelectedFoodSubCategory('All');
            setItemsPage(cached.page);
            setItems(cached.items);
            setItemsContextKey(targetContextKey);
            setItemsHasMore(cached.hasMore);
            setItemsRequestState({
                key: targetContextKey,
                status: 'success'
            });
            return;
        }

        setActiveTab('items');
        setSelectedItemCategory(department.category);
        setSelectedFoodSubCategory('All');
        setItemsPage(1);
        setItems([]);
        setItemsContextKey('');
        setItemsHasMore(false);
        setItemsRequestState({
            key: '',
            status: 'loading'
        });
        return;
    };

    const handleFoodSubCategorySelect = (subCat) => {
        const targetSubCategory = (subCat === 'All' || subCat === selectedFoodSubCategory) ? 'All' : subCat;
        const targetContextKey = buildItemsContextKey({
            itemCategory: selectedItemCategory,
            subCategory: targetSubCategory,
            search: searchQuery,
            eventType: eventFilter
        });
        const cached = sessionCacheRef.current.items[targetContextKey];
        if (cached) {
            setSelectedFoodSubCategory(targetSubCategory);
            setItemsPage(cached.page);
            setItems(cached.items);
            setItemsContextKey(targetContextKey);
            setItemsHasMore(cached.hasMore);
            setItemsRequestState({
                key: targetContextKey,
                status: 'success'
            });
            return;
        }
        setSelectedFoodSubCategory(targetSubCategory);
        setItemsPage(1);
        setItems([]);
        setItemsContextKey('');
        setItemsHasMore(false);
        setItemsRequestState({ key: '', status: 'loading' });
        fetchData('items', { subCategory: targetSubCategory, page: 1 });
    };

    const activityCategoryOptions = React.useMemo(() => {
        const available = new Set((activities || []).map(getActivityCategory));
        const ordered = ACTIVITY_CATEGORY_ORDER.filter(category => category === 'All' || available.has(category));
        return ordered.length > 1 ? ordered : ['All'];
    }, [activities]);

    const displayedActivities = React.useMemo(() => {
        if (selectedActivityCategories.includes('All') || selectedActivityCategories.length === 0) return activities;
        const selected = new Set(selectedActivityCategories);
        const filtered = (activities || []).filter(activity => selected.has(getActivityCategory(activity)));
        return filtered.length ? filtered : activities;
    }, [activities, selectedActivityCategories]);

    useEffect(() => {
        if (!showActivityFilterMenu) return undefined;

        const handlePointerDown = (event) => {
            if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
                setShowActivityFilterMenu(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [showActivityFilterMenu]);

    const toggleActivityCategory = (category) => {
        if (category === 'All') {
            setSelectedActivityCategories(['All']);
            return;
        }
        setSelectedActivityCategories(prev => {
            const current = prev.filter(item => item !== 'All');
            const next = current.includes(category)
                ? current.filter(item => item !== category)
                : [...current, category];
            const selectable = activityCategoryOptions.filter(item => item !== 'All');
            if (selectable.length > 0 && next.length === selectable.length) return ['All'];
            return next.length ? next : ['All'];
        });
    };

    const handleSelectItem = (item, type) => {
        const catalogKey = getCatalogKey(item, type);
        const isAlreadyAdded = addedKeys.has(catalogKey);

        // Normalize item to ShoppingList format
        let shoppingItem = {
            id: `cat_${type}_${item._id || Date.now()}`,
            name: item.name,
            description: item.description,
            catalog_ref: item.slug || item._id || item.id || item.name,
            catalog_id: item._id || item.id || item.name,
            catalog_type: type,
            catalog_key: catalogKey,
            workflow_origin: activeTab === 'activities' ? 'activities' : 'shopping',
            estimate_source: item.planningEstimate?.estimate_source || item.estimatePolicy?.confidence || 'CraftMyParty catalog estimate',
            sourcing_channels: item.sourcingPolicy?.channels || [],
            addedAt: new Date().toISOString()
        };

        if (type === 'activity') {
            const supplyCost = estimateActivitySupplyCost(item, eventData?.guest_count);
            const supplies = Array.isArray(item.supply_queries) && item.supply_queries.length
                ? item.supply_queries
                : (item.requiredSupplies || []).map(supply => supply.name).filter(Boolean);
            shoppingItem.category = 'Activities';
            shoppingItem.catalog_type = 'activity';
            shoppingItem.buy_price = formatCatalogPrice(supplyCost);
            shoppingItem.price_per_unit = formatCatalogPrice(supplyCost);
            shoppingItem.quantity = item.planningEstimate?.quantity || 1;
            shoppingItem.quantity_reason = item.planningEstimate?.quantity_reason || 'One activity setup';
            shoppingItem.image_url = item.image_url;
            shoppingItem.purchaseType = 'buy_only';
            shoppingItem.game_type = 'organized';
            shoppingItem.game_instructions = item.instructions || (item.tutorialSteps || []).join(' ');
            shoppingItem.supply_queries = supplies;
            shoppingItem.description = [
                item.description,
                item.instructions || item.tutorialSteps?.length ? `How to run it: ${parseInstructionSteps(item.tutorialSteps || item.instructions).join(' ')}` : '',
                supplies.length ? `Supplies to prep: ${supplies.slice(0, 5).join(', ')}.` : ''
            ].filter(Boolean).join(' ');
            shoppingItem.workflow_origin = 'catalog';
            shoppingItem.origin = 'catalog';
            shoppingItem.companion_items = buildActivitySupplyItems(item, shoppingItem, catalogKey);
        } else {
            // Standard catalog item
            shoppingItem.category = item.category;
            const estimate = item.planningEstimate || {};
            const unitPrice = estimate.unit_price_estimate ?? item.minPrice ?? item.maxPrice ?? 0;
            const totalPrice = estimate.total_estimate ?? unitPrice;
            shoppingItem.buy_price = formatCatalogPrice(totalPrice);
            shoppingItem.price_per_unit = formatCatalogPrice(unitPrice);
            shoppingItem.quantity = estimate.quantity || 1;
            shoppingItem.quantity_reason = estimate.quantity_reason || 'Estimated from catalog rule';
            shoppingItem.image_url = item.imageUrls?.[0];
            const purchaseType = String(item.purchaseType || 'buy_only');
            shoppingItem.rent_price = purchaseType === 'rent_only' ? formatCatalogPrice(item.minPrice || item.maxPrice || 0) : null;
            shoppingItem.rentable = purchaseType.includes('rent');
            shoppingItem.purchaseType = purchaseType;
            shoppingItem.workflow_origin = 'catalog';
            shoppingItem.origin = 'catalog';
        }

        const wasAdded = onSelectItem(shoppingItem);
        if (wasAdded !== false) {
            setAddedKeys(prev => {
                const next = new Set(prev);
                if (isAlreadyAdded) {
                    next.delete(catalogKey);
                } else {
                    next.add(catalogKey);
                }
                return next;
            });
        }
        // Don't close immediately, let them add more?
        // onClose();

        // Show success feedback (micro-interaction)
        // TODO: Toast interaction
    };

    if (!isOpen) return null;

    const activeDepartment = getCatalogDepartmentLabel(activeTab, selectedItemCategory);
    const searchPlaceholder = (() => {
        if (selectedItemCategory === 'Food & Cake') {
            if (!selectedFoodSubCategory || selectedFoodSubCategory === 'All') {
                return 'Search food...';
            }
            return `Search ${selectedFoodSubCategory.toLowerCase()}...`;
        }
        return `Search ${activeDepartment.toLowerCase()}...`;
    })();
    const hasContextFilters = Boolean(eventFilter || ageFilter);
    const currentItemsContextKey = buildItemsContextKey({
        itemCategory: selectedItemCategory,
        subCategory: selectedFoodSubCategory,
        search: searchQuery,
        eventType: eventFilter
    });
    const isCurrentItemsLoading =
        activeTab === 'items' &&
        itemsRequestState.status === 'loading';

    const isCurrentItemsLoaded =
        activeTab === 'items' &&
        itemsRequestState.status === 'success' &&
        itemsRequestState.key === currentItemsContextKey;

    const isActivitiesLoading =
        activeTab === 'activities' &&
        activitiesRequestState.status === 'loading';

    const isActivitiesLoaded =
        activeTab === 'activities' &&
        activitiesRequestState.status === 'success';

    const canRenderItems =
        activeTab === 'items' &&
        itemsContextKey === currentItemsContextKey &&
        items.length > 0;

    const filteredSelectedCatalogItems = selectedCatalogItems.filter(item => 
        !searchQuery || 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        (item.category && item.category.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const addedByYouCount = selectedCatalogItems.filter(item => getCatalogItemSourceLabel(item) === 'Added by you').length;
    const suggestedByPlanCount = selectedCatalogItems.filter(item => getCatalogItemSourceLabel(item) === 'Suggested by plan').length;
    const fallbackCount = selectedCatalogItems.filter(item => getCatalogItemSourceLabel(item) === 'Catalog item').length;

    const displayedSelectedItems = filteredSelectedCatalogItems.filter(item => {
        if (selectedSourceFilter === 'All') return true;
        return getCatalogItemSourceLabel(item) === selectedSourceFilter;
    });

    console.log('[Catalog] render context', {
        activeTab,
        selectedItemCategory,
        selectedFoodSubCategory,
        itemsContextKey,
        currentItemsContextKey,
        canRenderItems,
        itemsRequestState,
        activitiesRequestState,
        itemsCount: items.length,
        activitiesCount: activities.length,
        firstItems: items.slice(0, 3).map(item => ({
            name: item.name,
            category: item.category,
            kind: item.kind
        }))
    });

    const modal = (
        <div className="fixed inset-0 z-[99999] flex items-end justify-center bg-black/60 p-0 backdrop-blur-md md:items-center md:p-5" onClick={onClose}>
            <div
              className="flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-2xl md:h-[min(900px,calc(100vh-1.5rem))] md:w-[min(1180px,calc(100vw-1.5rem))] md:rounded-2xl md:flex-row"
              onClick={(e) => e.stopPropagation()}
            >

                {/* Sidebar Navigation */}
                <div className="hidden w-64 flex-col space-y-2 border-r border-gray-100 bg-gray-50 p-4 md:flex">
                    <button
                        type="button"
                        onClick={onClose}
                        className="mb-3 inline-flex h-10 w-fit items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 text-sm font-bold text-gray-800 shadow-sm transition hover:bg-gray-100"
                    >
                        <Icons.Back />
                        Back to plan
                    </button>

                    <h2 className="px-2 text-xl font-bold text-zinc-900">
                        Catalog
                    </h2>
                    <p className="px-2 pb-3 text-sm leading-relaxed text-gray-500">
                        Browse activities, decor, food, tableware, rentals, setup, and printables matched to your plan.
                    </p>

                    {selectedCatalogItems.length > 0 && (
                        <button
                            key="Selected"
                            type="button"
                            onClick={() => handleDepartmentSelect({ label: 'Selected from catalog', value: 'Selected', tab: 'selected', category: 'Selected' })}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-left ${activeTab === 'selected' ? 'bg-white shadow-md text-primary font-semibold' : 'text-gray-600 hover:bg-white/50'}`}
                        >
                            <Icons.Selected />
                            <span className="font-semibold text-sm">Selected</span>
                            <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-bold ${activeTab === 'selected' ? 'bg-primary/10 text-primary' : 'bg-gray-200 text-gray-600'}`}>
                                {selectedCatalogItems.length}
                            </span>
                        </button>
                    )}

                    {CATALOG_DEPARTMENTS.filter((department) => department.label !== 'All' && department.value !== 'all').map((department) => {
                        const selected = activeDepartment === department.label;
                        return (
                            <button
                                key={department.value}
                                type="button"
                                onClick={() => handleDepartmentSelect(department)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${selected ? 'bg-white shadow-md text-primary font-semibold' : 'text-gray-600 hover:bg-white/50'}`}
                            >
                                {department.tab === 'activities' ? <Icons.Activity /> : <Icons.Basket />}
                                <span>{department.label}</span>
                            </button>
                        );
                    })}

                    <div className="mt-auto border-t border-gray-100 pt-4">
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">Filters</div>
                        {/* Simple filters preview */}
                        {eventFilter && (
                            <div className="px-2 py-1 mb-2 bg-stone-100 text-stone-700 rounded text-sm whitespace-normal break-words">
                                {eventFilter}
                            </div>
                        )}
                        {ageFilter && (
                            <div className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm whitespace-normal break-words">
                                {ageFilter}
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex flex-col h-full overflow-hidden">

                    {/* Mobile Header */}
                    <div className="border-b border-gray-100 bg-white px-4 pt-[calc(0.85rem+env(safe-area-inset-top,0px))] pb-3 md:hidden">
                        <div className="mb-3 flex items-center justify-between gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="inline-flex h-10 items-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-sm font-extrabold text-gray-900 shadow-sm"
                            >
                                <Icons.Back />
                                Plan
                            </button>
                            <div className="min-w-0 flex-1 text-center">
                                <p className="whitespace-normal break-words text-base font-black text-gray-950">Catalog</p>
                                <p className="whitespace-normal break-words text-[11px] font-semibold text-gray-500">{activeDepartment}</p>
                            </div>
                            <ModalCloseButton onClick={onClose} />
                        </div>

                        <form onSubmit={handleSearch} className="relative">
                            <span className="pointer-events-none absolute left-3 top-1/2 z-10 flex -translate-y-1/2 items-center text-gray-400">
                                <Icons.Search />
                            </span>
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-12 w-full rounded-2xl border border-transparent bg-gray-50 pl-11 pr-4 text-base outline-none transition-all focus:border-primary/50 focus:bg-white focus:ring-4 focus:ring-primary/10"
                            />
                        </form>
                    </div>

                    {/* Desktop Header & Search */}
                    <div className="z-10 hidden gap-3 border-b border-gray-100 bg-white p-4 md:flex sm:p-6">
                        <form onSubmit={handleSearch} className="relative flex-1">
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 flex items-center text-gray-400 z-10">
                                <Icons.Search />
                            </span>
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-transparent focus:bg-white focus:border-primary/50 focus:ring-4 focus:ring-primary/10 rounded-xl transition-all outline-none"
                            />
                        </form>
                        <ModalCloseButton onClick={onClose} />
                    </div>

                    {/* Content Grid */}
                    <div className="flex-1 overflow-y-auto bg-gray-50/30 px-4 py-3 sm:p-6">
                        {/* Subtle catalog disclaimer */}
                        <div className="mb-4 text-center">
                            <p className="text-[11px] md:text-xs text-gray-400 max-w-xl mx-auto leading-relaxed">
                                Suggestions and prices are planning estimates only. CraftMyParty does not sell items, process purchases, or guarantee availability or pricing.
                            </p>
                        </div>

                        <div className="sticky top-0 z-10 -mx-4 mb-4 space-y-3 border-b border-gray-100 bg-white/95 px-4 py-3 shadow-sm backdrop-blur md:static md:mx-0 md:border-b-0 md:bg-transparent md:p-0 md:shadow-none">
                            <div className="flex gap-2 overflow-x-auto pt-1 pb-2 md:hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                {selectedCatalogItems.length > 0 && (
                                    <button
                                        key="Selected"
                                        type="button"
                                        onClick={() => handleDepartmentSelect({ label: 'Selected from catalog', value: 'Selected', tab: 'selected', category: 'Selected' })}
                                        className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold leading-none transition md:px-4 md:py-2 md:text-sm ${
                                            activeTab === 'selected'
                                                ? 'bg-primary text-white shadow-md shadow-primary/15'
                                                : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:text-primary'
                                        }`}
                                    >
                                        Selected ({selectedCatalogItems.length})
                                    </button>
                                )}
                                {CATALOG_DEPARTMENTS.filter((department) => department.label !== 'All' && department.value !== 'all').map((department) => {
                                    const selected = activeDepartment === department.label;
                                    return (
                                        <button
                                            key={department.value}
                                            type="button"
                                            onClick={() => handleDepartmentSelect(department)}
                                            className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold leading-none transition md:px-4 md:py-2 md:text-sm ${
                                                selected
                                                    ? 'bg-primary text-white shadow-md shadow-primary/15'
                                                    : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:text-primary'
                                            }`}
                                        >
                                            {department.label}
                                        </button>
                                    );
                                })}
                            </div>

{activeTab === 'items' && selectedItemCategory === 'Food & Cake' && (
    <div className="flex gap-2 overflow-x-auto px-1 pt-1 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {['All', 'Appetizers', 'Entrées', 'Desserts & Cake', 'Beverages', 'Snacks'].map((subCat) => {
            const selected = subCat === 'All'
                ? !selectedFoodSubCategory || selectedFoodSubCategory === 'All'
                : selectedFoodSubCategory === subCat;
            return (
                <button
                    key={subCat}
                    type="button"
                    onClick={() => handleFoodSubCategorySelect(subCat)}
                    className={`shrink-0 rounded-full px-3 py-1.5 text-[11px] font-semibold leading-none transition md:text-xs ${
                        selected
                            ? 'bg-zinc-900 text-white ring-1 ring-zinc-900'
                            : 'bg-white text-gray-600 ring-1 ring-gray-100 hover:text-gray-900'
                    }`}
                >
                                                {subCat}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {activeTab === 'activities' && !loading && (
                                <div className="space-y-3">
                                <div className="relative flex flex-wrap items-center gap-2" ref={filterMenuRef}>
                                    <button
                                        type="button"
                                        onClick={() => setShowActivityFilterMenu(prev => !prev)}
                                        className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 shadow-sm transition hover:border-primary/40 hover:text-primary"
                                    >
                                        <Icons.Filter />
                                        Filter categories
                                    </button>
                                    {!selectedActivityCategories.includes('All') && (
                                        <button
                                            type="button"
                                            onClick={() => setSelectedActivityCategories(['All'])}
                                            className="rounded-full px-3 py-2 text-sm font-bold text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                                        >
                                            Clear
                                        </button>
                                    )}
                                    {showActivityFilterMenu && (
                                        <div className="absolute left-0 top-full z-20 mt-2 w-72 rounded-2xl border border-gray-200 bg-white p-3 shadow-xl">
                                            <div className="mb-2 flex items-center justify-between gap-3 border-b border-gray-100 pb-2">
                                                <p className="text-xs font-black uppercase tracking-[0.16em] text-gray-500">Categories</p>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowActivityFilterMenu(false)}
                                                    className="rounded-full px-2 py-1 text-xs font-bold text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                                                >
                                                    Done
                                                </button>
                                            </div>
                                            <div className="grid max-h-72 gap-2 overflow-y-auto">
                                                {activityCategoryOptions.map((category) => {
                                                    const selected = selectedActivityCategories.includes(category);
                                                    return (
                                                        <button
                                                            key={category}
                                                            type="button"
                                                            onClick={() => toggleActivityCategory(category)}
                                                            className={`flex items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-bold transition ${
                                                                selected
                                                                    ? 'bg-primary text-white'
                                                                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            <span>{category}</span>
                                                            {selected && <span aria-hidden="true">✓</span>}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {(selectedActivityCategories.includes('All') ? ['All'] : selectedActivityCategories).map((category) => (
                                        <span
                                            key={category}
                                            className={`rounded-full px-3 py-1.5 text-xs font-bold ${
                                                category === 'All'
                                                    ? 'bg-primary text-white'
                                                    : 'bg-stone-100 text-stone-700 ring-1 ring-stone-200'
                                            }`}
                                        >
                                            {category}
                                        </span>
                                    ))}
                                </div>
                                </div>
                            )}

                            {hasContextFilters && (
                                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide md:hidden">
                                    {eventFilter && (
                                        <span className="shrink-0 rounded-full bg-stone-100 px-3 py-1 text-xs font-bold text-stone-700">
                                            {eventFilter}
                                        </span>
                                    )}
                                    {ageFilter && (
                                        <span className="shrink-0 rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
                                            {ageFilter}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {loading && (activeTab === 'activities' ? activities.length === 0 : items.length === 0) ? (
                            <div className="flex flex-col items-center justify-center h-64">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                                <p className="mt-4 text-gray-500 font-medium">
                                    {isCurrentItemsLoading ? 'Loading catalog items...' : isActivitiesLoading ? 'Loading activities...' : 'Discovering best options...'}
                                </p>
                            </div>
                        ) : (
                            <>
                                {activeTab === 'selected' && (
                                    <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-100 pb-3 w-full">
                                        <button
                                            type="button"
                                            onClick={() => setSelectedSourceFilter('All')}
                                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${selectedSourceFilter === 'All' ? 'bg-primary text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
                                        >
                                            All ({selectedCatalogItems.length})
                                        </button>
                                        {addedByYouCount > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setSelectedSourceFilter('Added by you')}
                                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${selectedSourceFilter === 'Added by you' ? 'bg-primary text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
                                            >
                                                Added by you ({addedByYouCount})
                                            </button>
                                        )}
                                        {suggestedByPlanCount > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setSelectedSourceFilter('Suggested by plan')}
                                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${selectedSourceFilter === 'Suggested by plan' ? 'bg-primary text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
                                            >
                                                Suggested by plan ({suggestedByPlanCount})
                                            </button>
                                        )}
                                        {fallbackCount > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setSelectedSourceFilter('Catalog item')}
                                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${selectedSourceFilter === 'Catalog item' ? 'bg-primary text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
                                            >
                                                Catalog items ({fallbackCount})
                                            </button>
                                        )}
                                    </div>
                                )}

                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">

                                    {/* SELECTED GRID */}
                                    {activeTab === 'selected' && displayedSelectedItems.map(item => {
                                        const displayName = getDisplayCatalogItemName(item);
                                        const priceDisplay = getSelectedItemPriceDisplay(item);
                                        const sourceLabel = getCatalogItemSourceLabel(item);

                                        // Badge style differentiation based on source label
                                        let badgeClass = 'bg-gray-50 text-gray-700 ring-1 ring-gray-600/10';
                                        if (sourceLabel === 'Added by you') {
                                            badgeClass = 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/10';
                                        } else if (sourceLabel === 'Suggested by plan') {
                                            badgeClass = 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/10';
                                        }

                                        return (
                                            <div key={item.id} className="group flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2.5 shadow-sm transition-all hover:shadow-md">
                                                <div className="min-w-0 flex-1">
                                                    <h3 className="whitespace-normal break-words text-sm font-semibold leading-snug text-gray-900 md:text-[15px]">{displayName}</h3>
                                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                                        <p className="mt-1 text-[11px] font-medium capitalize text-gray-500">
                                                            {item.category}
                                                        </p>
                                                        <span className={`mt-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${badgeClass}`}>
                                                            {sourceLabel}
                                                        </span>
                                                    </div>
                                                    {priceDisplay.primary ? (
                                                        <div className="mt-1.5 min-w-0">
                                                            <p className="text-[12px] font-semibold leading-tight text-gray-800">
                                                                {priceDisplay.primary}
                                                            </p>
                                                            {priceDisplay.secondary ? (
                                                                <p className="whitespace-normal break-words text-[10px] font-medium leading-tight text-gray-500">
                                                                    {priceDisplay.secondary}
                                                                </p>
                                                            ) : item.quantity_reason ? (
                                                                <p className="whitespace-normal break-words text-[10px] font-medium leading-tight text-gray-500">
                                                                    {item.quantity_reason}
                                                                </p>
                                                            ) : null}
                                                        </div>
                                                    ) : (
                                                        <div className="mt-1.5 min-w-0">
                                                            <p className="text-[11px] italic leading-tight text-gray-400">
                                                                Estimate unavailable
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => onSelectItem(item)}
                                                    aria-label={`Remove ${item.name} from plan`}
                                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-base font-bold transition-colors bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 hover:bg-red-50 hover:text-red-700 hover:ring-red-200"
                                                >
                                                    ✓
                                                </button>
                                            </div>
                                        );
                                    })}

                                    {activeTab === 'selected' && displayedSelectedItems.length === 0 && (
                                        <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
                                            <p className="text-gray-500 font-medium">
                                                {searchQuery ? `No selected items match "${searchQuery}"` : 'No items match the active source filter.'}
                                            </p>
                                        </div>
                                    )}

                                {/* ACTIVITIES GRID */}
                                {activeTab === 'activities' && displayedActivities.map(activity => {
                                    const isAdded = addedKeys.has(getCatalogKey(activity, 'activity'));
                                    return (
                                    <div key={activity.name} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col h-full">
                                        <div className="p-5 flex flex-col flex-1">
	                                            <div className="mb-2">
	                                                <h3 className="font-bold text-gray-900 text-lg">{activity.name}</h3>
                                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                                        <span className="inline-flex w-fit rounded-full bg-stone-100 px-2 py-1 text-xs font-bold text-stone-700">
                                                            {getActivityCategory(activity)}
                                                        </span>
                                                        {(activity.badges || []).slice(0, 2).map((badge) => (
                                                            <span key={badge} className="inline-flex w-fit rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700">
                                                                {badge}
                                                            </span>
                                                        ))}
                                                    </div>
	                                                {activity.energy_level && (
	                                                    <span className={`mt-2 inline-flex w-fit text-xs px-2 py-1 rounded-full ${activity.energy_level === 'High' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
	                                                        {activity.energy_level} Energy
	                                                    </span>
	                                                )}
	                                            </div>
	                                            <p className="text-sm text-gray-600 mb-4 flex-1">{activity.description}</p>
                                                {!!(activity.instructions || activity.tutorialSteps?.length) && (
                                                    <div className="mb-3 rounded-xl bg-stone-50 px-3 py-2 text-xs leading-relaxed text-stone-700">
                                                        <p className="font-bold">How it works</p>
                                                        <ol className="mt-1 space-y-1">
                                                            {parseInstructionSteps(activity.tutorialSteps || activity.instructions).map((step, index) => (
                                                                <li key={`${activity.name}-step-${index}`} className="grid grid-cols-[1.25rem_1fr] gap-1">
                                                                    <span className="font-bold text-stone-500">{index + 1}.</span>
                                                                    <span>{step}</span>
                                                                </li>
                                                            ))}
                                                        </ol>
                                                    </div>
                                                )}

	                                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-4 bg-gray-50 p-2 rounded-lg">
	                                                <span>⏱ {activity.duration || activity.duration_minutes || 20}m</span>
	                                                <span>•</span>
	                                                <span>Builds: {(activity.tags || activity.styleTags || []).slice(0, 4).join(', ')}</span>
	                                            </div>
                                                {((Array.isArray(activity.supply_queries) && activity.supply_queries.length > 0) || (Array.isArray(activity.requiredSupplies) && activity.requiredSupplies.length > 0)) && (
                                                    <p className="mb-4 text-xs text-gray-500">
                                                        Supplies: {(activity.supply_queries || activity.requiredSupplies?.map(supply => supply.name) || []).slice(0, 4).join(', ')}
                                                    </p>
                                                )}

	                                            <button
	                                                type="button"
	                                                onClick={() => handleSelectItem(activity, 'activity')}
	                                                className={`w-full py-2 border-2 font-semibold rounded-lg transition-colors ${
	                                                    isAdded 
	                                                        ? 'border-emerald-600 bg-emerald-50 text-emerald-700 hover:bg-red-50 hover:text-red-700 hover:border-red-200' 
	                                                        : 'border-primary text-primary hover:bg-primary hover:text-white'
	                                                }`}
	                                            >
	                                                {isAdded ? '✓ Added, tap to remove' : `Add activity · ${formatEstimatedCatalogPrice(estimateActivitySupplyCost(activity, eventData?.guest_count))}`}
	                                            </button>
                                        </div>
                                    </div>
                                    );
                                })}

                                {/* ITEMS GRID */}
{canRenderItems && dedupeCatalogItemsForDisplay(items).map(item => {
    const isAdded = addedKeys.has(getCatalogKey(item, 'item'));
    const priceDisplay = getCatalogPriceDisplay(item);
    return (
    <div key={item._id} className="group flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2.5 shadow-sm transition-all hover:shadow-md">
        <div className="min-w-0 flex-1">
            <h3 className="whitespace-normal break-words text-sm font-semibold leading-snug text-gray-900 md:text-[15px]">{getDisplayCatalogItemName(item)}</h3>
            {(item.kind || item.category) && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <p className="mt-1 text-[11px] font-medium capitalize text-gray-500">
                        {getCatalogItemTypeLabel(item, activeTab, selectedItemCategory)}
                    </p>
                    {item.kind === 'decor_item' && (
                        <span className="mt-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 ring-1 ring-emerald-600/10">
                            Style matched to your plan
                        </span>
                    )}
                </div>
            )}
            {priceDisplay.primary && (
                <div className="mt-1.5 min-w-0">
                    <p className="text-[12px] font-semibold leading-tight text-gray-800">
                        {priceDisplay.primary}
                    </p>
                    {priceDisplay.secondary && (
                        <p className="whitespace-normal break-words text-[10px] font-medium leading-tight text-gray-500">
                            {priceDisplay.secondary}
                        </p>
                    )}
                </div>
            )}
        </div>

        <button
            type="button"
            onClick={() => handleSelectItem(item, 'item')}
            aria-label={isAdded ? `Remove ${item.name} from plan` : `Add ${item.name} to plan`}
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-base font-bold transition-colors ${
                isAdded
                    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
            }`}
        >
            {isAdded ? '✓' : '+'}
        </button>
    </div>
    );
})}

                                {canRenderItems && itemsHasMore && (
                                    <div className="col-span-full flex flex-col items-center gap-3 pt-2">
                                        {loading && (
                                            <div className="flex justify-center py-2">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                                            </div>
                                        )}
                                        {!loading && (
                                            <button
                                                type="button"
                                                disabled={loading}
                                                onClick={() => fetchData('items', {
                                                    itemCategory: selectedItemCategory,
                                                    subCategory: selectedFoodSubCategory,
                                                    search: searchQuery,
                                                    eventType: eventFilter,
                                                    page: itemsPage + 1,
                                                    appendItems: true
                                                })}
                                                className="rounded-xl border border-gray-200 bg-white px-6 py-3 text-sm font-bold text-gray-800 shadow-sm transition hover:border-primary/40 hover:text-primary disabled:opacity-50"
                                            >
                                                Load more catalog picks
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* EMPTY STATE */}
                                {!loading && ((activeTab === 'activities' && !isActivitiesLoading && isActivitiesLoaded && displayedActivities.length === 0) || (activeTab === 'items' && !isCurrentItemsLoading && isCurrentItemsLoaded && items.length === 0)) && (
                                    <div className="col-span-full flex flex-col items-center justify-center py-20 text-gray-400">
                                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                            <Icons.Search />
                                        </div>
                                        <p className="text-lg font-medium text-gray-500">No results found</p>
                                        <p className="text-sm">Try adjusting your filters or search query</p>
                                    </div>
                                )}
                            </div>
                        </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const portalTarget = typeof document !== 'undefined' ? document.body : null;
    return portalTarget ? createPortal(modal, portalTarget) : modal;
};

export default CatalogExplorer;
